import type { NextRequest } from 'next/server';
import { joinMountedUrl } from '@/lib/site';

/**
 * Laravel base URL for Next.js Route Handlers (server-side proxy).
 * Prefer LARAVEL_API_URL — it is read at runtime (no rebuild) unlike NEXT_PUBLIC_*.
 */
export function getLaravelBackendUrl(): string {
  const raw =
    process.env.LARAVEL_API_URL?.trim() ||
    process.env.NEXT_PUBLIC_API_URL?.trim() ||
    process.env.NEXT_PUBLIC_LARAVEL_URL?.trim() ||
    'http://localhost:8000';
  const cleaned = raw.replace(/\/$/, '');
  /**
   * Laravel application root URL (no trailing slash).
   * Include the subfolder when the app is mounted under one (e.g. `https://site.com/api`
   * with APP_PATH=api). Do not strip `/api` — that breaks `/login`, `/register`, and JSON routes.
   */
  return cleaned;
}

export function buildLaravelBackendUrl(path: string): string {
  return joinMountedUrl(getLaravelBackendUrl(), path);
}

/**
 * When Laravel sets `Domain=api.example.com`, browsers ignore the cookie on the Next.js host.
 * Strip `Domain` so the cookie is host-only on the Next origin.
 */
export function rewriteSetCookieForNextHost(cookie: string, requestHost?: string | null): string {
  // If Laravel is configured with SESSION_DOMAIN=.example.com we MUST keep it,
  // otherwise the session won't be shared when we navigate to Laravel pages.
  // Only strip when Laravel sets a non-parent domain (e.g. api.example.com).
  if (/;\s*domain=\s*\./i.test(cookie)) return cookie;
  if (!requestHost) return cookie.replace(/;\s*domain=[^;]+/gi, '');
  const host = requestHost.split(':')[0].toLowerCase();
  const m = cookie.match(/;\s*domain=([^;]+)/i);
  if (!m) return cookie;
  const domain = m[1].trim().toLowerCase();
  if (domain === host) return cookie;
  // If cookie domain is a parent of host, keep it (rare, but safe).
  if (host.endsWith(`.${domain.replace(/^\./, '')}`)) return cookie;
  return cookie.replace(/;\s*domain=[^;]+/gi, '');
}

/**
 * Robustly read all Set-Cookie values from upstream responses.
 * Some runtimes expose headers.getSetCookie(), others only expose a combined set-cookie string.
 */
export function getResponseSetCookies(headers: Headers): string[] {
  const setCookieHeaders = (headers as Headers & { getSetCookie?: () => string[] }).getSetCookie?.();
  if (Array.isArray(setCookieHeaders) && setCookieHeaders.length > 0) return setCookieHeaders;

  const combined = headers.get('set-cookie');
  if (!combined) return [];
  // Split combined Set-Cookie values on cookie boundaries, not Expires date commas.
  return combined
    .split(/,(?=\s*[A-Za-z0-9!#$%&'*+.^_`|~-]+=)/g)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Forward cookies and headers Laravel expects for session auth and CSRF behind a same-origin proxy.
 */
export function getLaravelProxyHeaders(request: NextRequest): Record<string, string> {
  const cookie = request.headers.get('cookie') || '';
  const csrf = request.headers.get('x-csrf-token') || request.headers.get('X-CSRF-TOKEN') || '';
  const xsrf =
    request.headers.get('x-xsrf-token') ||
    request.headers.get('X-XSRF-TOKEN') ||
    '';
  const auth = request.headers.get('authorization') || '';

  const headers: Record<string, string> = {
    Cookie: cookie,
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  if (csrf) headers['X-CSRF-TOKEN'] = csrf;
  if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
  if (auth) headers['Authorization'] = auth;

  return headers;
}
